import * as React from 'react';
import { Page, Text, View, Image, Font } from '@react-pdf/renderer';
import i18n, { getDateFormatOptions, formatStreet } from '@/utils/i18n';
import Break from './ReactPdfBr';
import ReactPdfTable from './ReactPdfTable';
import {
  MerchantOrder,
  MerchantOrderItemOption,
  Revision,
} from '../EditMerchantOrder/HandleStatusChange';
import { styles } from './Styles';
// @ts-ignore
import regular from '../../fonts/Roboto-Regular.ttf?url';
// @ts-ignore
import bold from '../../fonts/Roboto-Bold.ttf?url';
import { Communicator } from '../../tools/Communication';

export interface ContactInfo {
  storeId: string;
  privateContactName?: string;
  privateContactPhone?: string;
  privateContactEmail?: string;
}

const MerchantOrderItemStatus = {
  Undefined: 0,
  Pickup: 1,
  Delivery: 2,
};

export function registerFont() {
  Font.register({
    family: 'Roboto',
    fonts: [
      {
        src: regular,
      },
      {
        src: bold,
        fontWeight: 'bold',
      },
    ],
  });
}

function getPartnerId(order: MerchantOrder) {
  // Event-based orders don't have a targetStoreId
  if (!order.targetStoreId) {
    return order.eventId + ' / ' + order.eventAccountId;
  }
  let result = order.targetStoreId.split('_')[1];
  if (order.storeTier != null) {
    result = `${result}_${order.storeTier}`;
  }
  return result;
}

export function getHandTo(
  translate: Function,
  recipient: { firstName: string; lastName: string },
) {
  const { firstName, lastName } = recipient;
  if (
    (firstName != null && firstName.trim().length != 0) ||
    (lastName != null && lastName.trim().length != 0)
  ) {
    let name = firstName != null ? firstName + ' ' : '';
    name = lastName != null ? name + lastName : name;
    return (
      <Text style={styles.address}>
        {`${translate('MerchantOrder:handTo')} ${name}`}
      </Text>
    );
  }
  return null;
}

function getContactInfo(revision: Revision, contactInfoIn: ContactInfo | null) {
  const contactInfo: Omit<ContactInfo, 'storeId'> = {
    privateContactName:
      revision.deliveryAddress.firstName +
      ' ' +
      revision.deliveryAddress.lastName,
    privateContactPhone: revision.deliveryAddress.phoneNumber,
    privateContactEmail: revision.deliveryAddress.email,
  };

  if (contactInfoIn != null) {
    contactInfo.privateContactEmail =
      contactInfo.privateContactEmail || contactInfoIn.privateContactEmail;
    contactInfo.privateContactName =
      contactInfo.privateContactName || contactInfoIn.privateContactName;
    contactInfo.privateContactPhone =
      contactInfo.privateContactPhone || contactInfoIn.privateContactPhone;
  }
  return contactInfo;
}

async function MerchantOrderPdf(
  merchantOrder: MerchantOrder,
  keyCities: string[],
  appuserId: string | null,
  userName: string | null,
  contactInfoIn: ContactInfo | null,
  merchantOrderItemOptions: MerchantOrderItemOption[],
) {
  const { revision } = merchantOrder;

  const contactInfo = getContactInfo(revision, contactInfoIn);
  const translate = i18n.getFixedT(revision.language);
  const charWidth = 5.5;
  // For event-based orders without targetStoreId, use a default value
  const partnerIdLength = merchantOrder.targetStoreId
    ? merchantOrder.targetStoreId.split('_')[1].length
    : 3;
  const valueWidth = partnerIdLength * charWidth;
  const keyWidth =
    (Math.max(
      // translate('MerchantOrder:orderDate').length,
      translate('Common:partner').length,
      translate('MerchantOrder:deliveryNote').length,
      translate('MerchantOrder:deliveryAt').length,
    ) +
      1) *
    charWidth;

  const userNameReplaced = (userName || '').replace('VYTALadmin_', '');
  const items = revision.items
    .filter((i) => parseInt(i.amount as string) > 0)
    .map((i) => ({
      item: {
        ...i,
        name:
          merchantOrderItemOptions.find((o) => o.typeId === i.typeId)?.name ??
          i18n.t('Common:unknown'),
      },
      option: merchantOrderItemOptions.find((opt) => opt.typeId === i.typeId),
    }))
    .sort((a, b) => (b.option?.orderKey || 0) - (a.option?.orderKey || 0))
    .map((i) => i.item);

  async function getUserQrCodeUrl() {
    const res = await Communicator.Get(
      `/Users/QrCodeForUser/${appuserId}/true`,
      {},
    );
    return res.arrayBuffer();
  }

  const qrCodeData = await getUserQrCodeUrl();

  return (
    <Page size="A4" style={{ padding: '15mm' }}>
      {/* <Text style={styles.title}>{translate('MerchantOrder:deliveryNote')}</Text> */}

      <View style={[styles.spaceBetween, { marginTop: 5 }]}>
        <Image style={{ height: 69 }} src="/vytal_logo.png" />
        <View
          style={[
            styles.deliveryNoteInfoContainer,
            { width: valueWidth + keyWidth },
          ]}
        >
          <View style={styles.row}>
            <Text style={[styles.deliveryNoteInfo, { width: keyWidth }]}>
              {`${translate('MerchantOrder:deliveryAt')}: `}
            </Text>
            <Text style={[styles.deliveryNoteInfo, { width: valueWidth }]}>
              {revision.orderExecutionDate
                ? new Date(revision.orderExecutionDate).toLocaleDateString(
                    revision.language || 'de',
                    getDateFormatOptions(false, revision.language || 'de'),
                  )
                : '-'}
            </Text>
          </View>
          <View style={styles.row}>
            <Text
              style={[styles.deliveryNoteInfo, { width: keyWidth }]}
            >{`${translate('Common:partner')}: `}</Text>
            <Text style={[styles.deliveryNoteInfo, { width: valueWidth }]}>
              {getPartnerId(merchantOrder)}
            </Text>
          </View>
          <View style={styles.row}>
            <Text
              style={[styles.deliveryNoteInfo, { width: keyWidth }]}
            >{`${translate('MerchantOrder:deliveryNote')}: `}</Text>
            <Text style={[styles.deliveryNoteInfo, { width: valueWidth }]}>
              {String(merchantOrder.deliveryNoteId).padStart(
                getPartnerId(merchantOrder).length - 3,
                '0',
              )}
            </Text>
          </View>
          {userNameReplaced.trim() !== '' && (
            <View style={styles.row}>
              <Text
                style={[styles.deliveryNoteInfo, { width: keyWidth }]}
              >{`${translate('MerchantOrder:orderer')}: `}</Text>
              <Text style={[styles.deliveryNoteInfo, { width: valueWidth }]}>
                {userNameReplaced}
              </Text>
            </View>
          )}
          <View style={styles.row}>
            <Text
              style={[styles.deliveryNoteInfo, { width: keyWidth }]}
            >{`${translate('MerchantOrder:type')}: `}</Text>
            <Text style={[styles.deliveryNoteInfo, { width: valueWidth }]}>
              {merchantOrder.storeType}
            </Text>
          </View>
        </View>
      </View>
      <Break />
      <View style={styles.spaceBetween}>
        <View>
          <Text style={styles.subtitle}>
            {`${translate(`MerchantOrder:Type:${revision.type}`)} ${
              keyCities.includes(merchantOrder.cityId)
                ? translate('Common:city')
                : translate('Common:region')
            }`}
          </Text>
          {/* <Text style={{fontWeight: 'bold', marginBottom: 5}}>{translate('Common:partner')}</Text> */}
          <View style={styles.addressContainer}>
            <Text style={styles.address}>
              {revision.deliveryAddress.company}
            </Text>
            {getHandTo(translate, {
              firstName: revision.deliveryAddress.firstName,
              lastName: revision.deliveryAddress.lastName,
            })}
            <Text style={styles.address}>{`${formatStreet(
              revision.deliveryAddress.streetname,
              revision.deliveryAddress.streetno,
            )}`}</Text>
            {revision.language === 'de' &&
            revision.deliveryAddress.additionalInfo != null &&
            revision.deliveryAddress.additionalInfo.trim() !== '' ? (
              <Text style={styles.address}>
                {revision.deliveryAddress.additionalInfo}
              </Text>
            ) : null}
            <Text
              style={styles.address}
            >{`${revision.deliveryAddress.zip} ${revision.deliveryAddress.city}`}</Text>
            {revision.deliveryAddress.country != null &&
              revision.deliveryAddress.country !== 'DE' && (
                <Text style={styles.address}>
                  {translate(
                    `Common:Country:${revision.deliveryAddress.country}`,
                  )}
                </Text>
              )}
            {revision.language !== 'de' &&
            revision.deliveryAddress.additionalInfo != null &&
            revision.deliveryAddress.additionalInfo.trim() !== '' ? (
              <Text style={styles.address}>{`${translate(
                'MerchantOrder:additionalInfo',
              )}: ${revision.deliveryAddress.additionalInfo}`}</Text>
            ) : null}
          </View>
        </View>
        {appuserId != null ? (
          <Image
            style={styles.qrCode}
            src={{ data: Buffer.from(qrCodeData), format: 'png' }}
          />
        ) : null}
      </View>

      {items.some((i) => i.status !== MerchantOrderItemStatus.Delivery) && (
        <Text style={styles.title}>ABHOLUNG</Text>
      )}
      {items.every((i) => i.status !== MerchantOrderItemStatus.Pickup) && (
        <View style={{ marginTop: 10 }}>
          <Text style={styles.body}>
            {translate('MerchantOrder:salutation', {
              company: revision.deliveryAddress.company,
            })}
          </Text>
          <Text style={styles.body}>{translate('MerchantOrder:body1')}</Text>
          <Text style={[styles.body, { marginLeft: 20 }]}>
            {translate('MerchantOrder:requests')}
          </Text>
          <Text style={styles.body}>{translate('MerchantOrder:body2')}</Text>
          <Text style={styles.body}>{translate('MerchantOrder:body3')}</Text>
        </View>
      )}
      <ReactPdfTable items={items} translate={translate} />

      {revision.comment != null && revision.comment.trim() !== '' ? (
        <Text style={styles.body}>{revision.comment}</Text>
      ) : null}
      <Text style={[styles.body, { marginTop: 10 }]}>
        {translate('MerchantOrder:greetings')}
      </Text>

      <View style={[styles.addressContainer, styles.footer]}>
        <Break />
        <View style={styles.spaceBetween}>
          {revision.language !== 'fr' && (
            <View style={{ marginLeft: 5 }}>
              <Text style={styles.address}>{'Vytal Global GmbH'}</Text>
              <Text style={styles.address}>{'Pilgrimstraße 6'}</Text>
              <Text style={styles.address}>{'50674 Köln'}</Text>
            </View>
          )}
          {revision.language === 'fr' && (
            <View style={{ marginLeft: 5 }}>
              <Text style={styles.address}>{'Vytal France SAS'}</Text>
              <Text style={styles.address}>{'80 rue des Haies'}</Text>
              <Text style={styles.address}>{'75020 Paris, France'}</Text>
            </View>
          )}
          {revision.language !== 'fr' && (
            <View>
              {contactInfo.privateContactName != null && (
                <Text style={styles.address}>{`${translate(
                  'MerchantOrder:contact',
                )}: ${contactInfo.privateContactName}`}</Text>
              )}
              {contactInfo.privateContactPhone != null && (
                <Text style={styles.address}>{`${translate(
                  'Common:phonenumber',
                )}: ${contactInfo.privateContactPhone}`}</Text>
              )}
              {contactInfo.privateContactEmail != null && (
                <Text style={styles.address}>{`${translate('Common:email')}: ${
                  contactInfo.privateContactEmail
                }`}</Text>
              )}
            </View>
          )}
          {revision.language === 'fr' && (
            <View>
              <View style={{ marginLeft: 5 }}>
                <Text style={styles.address}>
                  {'Adresse de retour: Vytal Global GmbH'}
                </Text>
                <Text style={styles.address}>{'Pilgrimstraße 6'}</Text>
                <Text style={styles.address}>{'50674 Köln'}</Text>
                <Text style={styles.address}>{'Germany'}</Text>
              </View>
            </View>
          )}
        </View>
      </View>
    </Page>
  );
}

export default MerchantOrderPdf;
